import { ShieldCheck, Award, Globe2, Beaker } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api, { resolveImage } from "@/lib/api";

const valueIcons = [ShieldCheck, Beaker, Globe2, Award];

export default function About() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/page-content/about").then(({ data }) => setContent(data));
  }, []);

  if (!content) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Yükleniyor...</div>;
  }

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="bg-[#0A2540] text-white py-24" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 lg:px-12">
          <div className="text-xs uppercase tracking-[0.25em] text-[#0066FF] font-semibold mb-4">
            {content.hero_eyebrow}
          </div>
          <h1 className="heading-font text-4xl md:text-5xl lg:text-6xl max-w-4xl leading-[1.05]">
            {content.hero_title}
          </h1>
          <p className="mt-8 text-lg text-white/70 max-w-2xl leading-relaxed whitespace-pre-line">
            {content.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <img
              src={resolveImage(content.story_image)}
              alt="Laboratuvar"
              className="w-full aspect-[4/5] object-cover"
            />
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.25em] text-[#0066FF] font-semibold mb-3">
              {content.story_eyebrow}
            </div>
            <h2 className="heading-font text-3xl md:text-4xl text-[#0A2540] leading-tight">
              {content.story_title}
            </h2>
            <div className="mt-6 space-y-5 text-slate-600 leading-relaxed">
              {content.story_p1 && <p>{content.story_p1}</p>}
              {content.story_p2 && <p>{content.story_p2}</p>}
              {content.story_p3 && <p>{content.story_p3}</p>}
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4">
              {[
                { v: content.stat1_v, l: content.stat1_l },
                { v: content.stat2_v, l: content.stat2_l },
                { v: content.stat3_v, l: content.stat3_l },
              ].map((s, i) => (
                <div key={i} className="border-l-2 border-[#0066FF] pl-4">
                  <div className="heading-font text-3xl text-[#0A2540]">{s.v}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 lg:px-12">
          <h2 className="heading-font text-3xl md:text-4xl text-[#0A2540] mb-12 max-w-3xl">
            {content.values_title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200">
            {[1, 2, 3, 4].map((n, i) => {
              const Icon = valueIcons[i];
              return (
                <div key={n} className="bg-white p-8" data-testid={`value-card-${i}`}>
                  <Icon size={28} className="text-[#0066FF] mb-5" />
                  <h3 className="heading-font text-lg text-[#0A2540] mb-2">{content[`value${n}_title`]}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{content[`value${n}_desc`]}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#0066FF] font-semibold mb-3">
              {content.cert_eyebrow}
            </div>
            <h2 className="heading-font text-3xl md:text-4xl text-[#0A2540] leading-tight">
              {content.cert_title}
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed whitespace-pre-line">
              {content.cert_description}
            </p>
          </div>
          <div className="space-y-4">
            {[
              { code: "ISO 9001:2008", title: "Kalite Yönetim Sistemi" },
              { code: "ISO 14001", title: "Çevre Yönetim Sistemi" },
              { code: "OHSAS 18001:2007", title: "İş Sağlığı ve Güvenliği" },
            ].map((c, i) => (
              <div
                key={i}
                className="border border-slate-200 p-6 flex items-center gap-5 hover:border-[#0066FF] transition-colors"
                data-testid={`cert-${i}`}
              >
                <div className="w-14 h-14 bg-[#0A2540] text-white flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={24} />
                </div>
                <div>
                  <div className="heading-font text-lg text-[#0A2540]">{c.code}</div>
                  <div className="text-sm text-slate-600">{c.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 lg:px-12 text-center">
          <h2 className="heading-font text-3xl md:text-4xl text-[#0A2540]">
            {content.cta_title}
          </h2>
          <Link
            to="/iletisim"
            className="inline-flex items-center gap-2 mt-8 bg-[#0A2540] hover:bg-[#0066FF] text-white px-8 py-4 font-semibold transition-colors"
            data-testid="about-cta-contact"
          >
            İletişime Geçin
          </Link>
        </div>
      </section>
    </div>
  );
}
